import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, MapPin, AlertTriangle, Scale } from 'lucide-react';
import { caseResults } from '@/data/caseResults';

// Cross-links to other case results at the bottom of a single case study page.
// Filters out the current case, keeps visitors on-site, and passes internal
// link equity across the case results cluster (SEO benefit).
const RelatedCaseResults = ({ currentSlug }) => {
  const others = caseResults.filter((c) => c.slug !== currentSlug);
  if (others.length === 0) return null;

  return (
    <section className="py-14 md:py-16 bg-white border-t border-slate-100" data-testid="related-case-results">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">
              <Award className="w-4 h-4" /> Other Case Results
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              More Real Outcomes for Injured Clients
            </h2>
          </div>
          <Link
            to="/case-results"
            data-testid="related-case-results-hub-link"
            className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:gap-3 transition-all"
          >
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {others.slice(0, 2).map((c) => (
            <Link
              key={c.slug}
              to={c.path}
              data-testid={`related-case-card-${c.slug}`}
              className="group bg-slate-50 hover:bg-teal-50 border border-slate-100 rounded-2xl p-6 transition-all hover:-translate-y-0.5"
            >
              <span className="inline-flex items-center gap-1.5 bg-white text-teal-700 px-3 py-1 rounded-full text-[11px] font-semibold tracking-widest uppercase mb-3 border border-teal-100">
                {c.practiceArea}
              </span>
              <h3 className="font-heading text-lg md:text-xl font-bold text-secondary group-hover:text-teal-700 transition-colors mb-2 leading-snug">
                {c.headline}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                {c.summary}
              </p>
              <dl className="grid grid-cols-1 gap-1.5 text-xs text-slate-600 mb-4">
                <div className="flex items-start gap-1.5">
                  <Scale className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span><span className="font-semibold">Result:</span> {c.result}</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span><span className="font-semibold">Injury:</span> {c.injury}</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span><span className="font-semibold">Location:</span> {c.location}</span>
                </div>
              </dl>
              <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
                Read the Case Study <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedCaseResults;
